import PushNotificationPreferenceAPI from 'api/pushNotificationPreference.api';
import { updateLoadingState } from 'store/app.slice';
import {
  setPreferences,
  updatePreference,
} from 'store/pushNotificationPreference.slice';
import { showNotification } from 'store/notification.slice';
import { BaseHandler } from './base.handler';

export default class PushNotificationPreferenceHandler extends BaseHandler {
  private readonly api: PushNotificationPreferenceAPI;

  constructor() {
    super();

    this.api = new PushNotificationPreferenceAPI();
  }

  async getAll(userId: string) {
    this.dispatch(updateLoadingState(true));
    try {
      const { data } = await this.api.getAll(userId);
      this.dispatch(setPreferences(data));
      return data;
    } catch (_) {
      // show snackbar error
    } finally {
      this.dispatch(updateLoadingState(false));
    }
  }

  async updatePreference(payload: {
    customerId: string;
    userId: string;
    tpmsAlertsEnabled: boolean;
  }) {
    // optimistic update — flip immediately, revert on failure
    this.dispatch(updatePreference(payload));
    try {
      await this.api.update(payload);
    } catch (_) {
      this.dispatch(
        updatePreference({
          customerId: payload.customerId,
          tpmsAlertsEnabled: !payload.tpmsAlertsEnabled,
        })
      );
      this.dispatch(
        showNotification({
          message: 'Could not save notification preference, please try again',
          type: 'error',
        })
      );
    }
  }

  // Persists a batch of pending toggles at once (Save button flow). Only
  // customers that actually saved get committed to the store; failures are
  // reported so the caller can leave those switches in their pending state.
  async savePreferences(
    userId: string,
    changes: Array<{ customerId: string; tpmsAlertsEnabled: boolean }>
  ): Promise<{ failedCustomerIds: string[] }> {
    this.dispatch(updateLoadingState(true));
    try {
      const results = await Promise.allSettled(
        changes.map(async (change) => {
          return await this.api.update({
            customerId: change.customerId,
            userId,
            tpmsAlertsEnabled: change.tpmsAlertsEnabled,
          });
        })
      );

      const failedCustomerIds: string[] = [];
      results.forEach((result, index) => {
        const change = changes[index];
        if (result.status === 'fulfilled') {
          this.dispatch(updatePreference(change));
        } else {
          failedCustomerIds.push(change.customerId);
        }
      });

      if (failedCustomerIds.length) {
        this.dispatch(
          showNotification({
            message: `Could not save preference for ${failedCustomerIds.length} site(s), please try again`,
            type: 'error',
          })
        );
      } else {
        this.dispatch(
          showNotification({
            message: 'Notification preferences saved successfully',
            type: 'success',
          })
        );
      }

      return { failedCustomerIds };
    } finally {
      this.dispatch(updateLoadingState(false));
    }
  }

  // Admin-managing-sub-clients'-users flow, shown as its own section on the
  // Notification Preferences page. Deliberately kept fully separate from
  // getAll/savePreferences/the pushNotificationPreference Redux slice above —
  // that slice holds only the CALLER's own self-service preferences keyed by
  // customerId, so mixing another user's preference into it (matching only on
  // customerId) could wrongly overwrite the caller's own displayed value for
  // the same customer. The caller and their site's other users are unrelated
  // data as far as this page's state is concerned.
  async getManagedUsers(userId: string) {
    this.dispatch(updateLoadingState(true));
    try {
      const { data } = await this.api.getManagedUsers(userId);
      return data;
    } catch (_) {
      this.dispatch(
        showNotification({
          message: 'Could not load managed users, please try again',
          type: 'error',
        })
      );
      return [];
    } finally {
      this.dispatch(updateLoadingState(false));
    }
  }

  // Persists a batch of managed-user toggles at once. Returns which
  // (userId, customerId) pairs failed so the caller can leave just those
  // pending rather than losing every change on a partial failure.
  async saveManagedPreferences(
    changes: Array<{
      userId: string;
      customerId: string;
      tpmsAlertsEnabled: boolean;
    }>
  ): Promise<{ failedKeys: string[] }> {
    this.dispatch(updateLoadingState(true));
    try {
      const results = await Promise.allSettled(
        changes.map(async (change) => {
          return await this.api.update(change);
        })
      );

      const failedKeys: string[] = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const change = changes[index];
          failedKeys.push(`${change.userId}:${change.customerId}`);
        }
      });

      if (failedKeys.length) {
        this.dispatch(
          showNotification({
            message: `Could not save preference for ${failedKeys.length} user(s), please try again`,
            type: 'error',
          })
        );
      } else {
        this.dispatch(
          showNotification({
            message: 'Notification preferences saved successfully',
            type: 'success',
          })
        );
      }

      return { failedKeys };
    } finally {
      this.dispatch(updateLoadingState(false));
    }
  }
}
